import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { postsService } from '../services/posts';
import { CalendarIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: post, isLoading, error } = useQuery({
    queryKey: ['post', slug],
    queryFn: () => postsService.getPostBySlug(slug!),
    enabled: !!slug,
  });

  // Scroll to top when post loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [post]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c69c70]"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-light text-gray-900 mb-4">Article non trouvé</h1>
          <Link 
            to="/journal" 
            className="inline-flex items-center gap-2 text-[#497aab] hover:text-[#c69c70] transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Retour au journal
          </Link>
        </div>
      </div>
    );
  }

  const featuredImage = postsService.getFeaturedImage(post);
  
  // SEO metadata from Rank Math or Yoast
  const seoTitle = post.rank_math?.title || post.yoast_head_json?.title || post.title.rendered;
  const seoDescription = post.rank_math?.description || post.yoast_head_json?.description || postsService.getExcerpt(post);
  const ogImage = post.rank_math?.og_image || post.yoast_head_json?.og_image?.[0]?.url || featuredImage || '';
  const canonical = post.rank_math?.canonical || `https://aupiho.com/blog/${slug}`;

  return (
    <>
      <Helmet>
        {/* Title */}
        <title>{seoTitle} - Aupiho Joaillerie</title>
        
        {/* Meta Description */}
        <meta name="description" content={seoDescription} />
        
        {/* Canonical */}
        <link rel="canonical" href={canonical} />
        
        {/* Open Graph */}
        <meta property="og:title" content={post.rank_math?.og_title || seoTitle} />
        <meta property="og:description" content={post.rank_math?.og_description || seoDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.rank_math?.twitter_title || seoTitle} />
        <meta name="twitter:description" content={post.rank_math?.twitter_description || seoDescription} />
        <meta name="twitter:image" content={post.rank_math?.twitter_image || ogImage} />
        
        {/* Robots */}
        {post.rank_math?.robots && (
          <meta name="robots" content={post.rank_math.robots.join(', ')} />
        )}
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section with Featured Image */}
        <section className="relative h-[500px] overflow-hidden">
          {featuredImage ? (
            <>
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ 
                  backgroundImage: `url(${featuredImage})`,
                  backgroundAttachment: 'fixed'
                }}
              ></div>
              <div className="absolute inset-0 bg-black/60"></div>
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#497aab] to-[#5a8bc4]"></div>
          )}
          
          <div className="relative h-full container mx-auto px-4 flex items-center">
            <div className="max-w-4xl mx-auto text-center text-white">
              <div className="flex items-center justify-center gap-2 mb-6">
                <CalendarIcon className="h-5 w-5" />
                <time className="text-sm font-medium">
                  {postsService.formatDate(post.date)}
                </time>
              </div>
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 tracking-wide"
                dangerouslySetInnerHTML={{ __html: post.title.rendered }}
              />
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-200">
          <div className="container mx-auto px-4 py-4">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-gray-600 hover:text-[#c69c70] transition-colors">
                Accueil
              </Link>
              <span className="text-gray-400">/</span>
              <Link to="/journal" className="text-gray-600 hover:text-[#c69c70] transition-colors">
                Journal
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-gray-900">{post.title.rendered}</span>
            </nav>
          </div>
        </div>

        {/* Article Content */}
        <article className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
              {/* Content */}
              <div 
                className="article-content
                  prose prose-lg max-w-none
                  prose-headings:font-light prose-headings:text-gray-900
                  prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:text-[#497aab] prose-h2:mb-4 prose-h2:mt-8 prose-h2:font-semibold
                  prose-h3:text-xl md:prose-h3:text-2xl prose-h3:text-[#c69c70] prose-h3:mb-3 prose-h3:mt-6 prose-h3:font-medium
                  prose-h4:text-lg md:prose-h4:text-xl prose-h4:text-gray-800 prose-h4:mb-2 prose-h4:mt-4 prose-h4:font-medium
                  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-base
                  prose-a:text-[#497aab] prose-a:underline hover:prose-a:text-[#c69c70]
                  prose-strong:text-gray-900 prose-strong:font-semibold
                  prose-em:text-gray-700 prose-em:italic
                  prose-img:rounded-xl prose-img:shadow-lg prose-img:my-6 prose-img:mx-auto
                  prose-blockquote:border-l-4 prose-blockquote:border-[#c69c70] prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-gray-700 prose-blockquote:my-6
                  prose-ul:list-disc prose-ul:ml-6 prose-ul:my-4 prose-ul:space-y-2
                  prose-ol:list-decimal prose-ol:ml-6 prose-ol:my-4 prose-ol:space-y-2
                  prose-li:text-gray-700 prose-li:leading-relaxed prose-li:text-base
                  prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm prose-code:text-gray-800
                  prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto
                  prose-table:w-full prose-table:border-collapse prose-table:my-6
                  prose-thead:bg-gray-100
                  prose-th:border prose-th:border-gray-300 prose-th:px-4 prose-th:py-2 prose-th:text-left prose-th:font-semibold
                  prose-td:border prose-td:border-gray-300 prose-td:px-4 prose-td:py-2
                  prose-hr:border-gray-300 prose-hr:my-8"
                dangerouslySetInnerHTML={{ __html: post.content.rendered }}
              />

              {/* Back to Journal */}
              <div className="mt-16 pt-8 border-t-2 border-gray-200">
                <Link 
                  to="/journal" 
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#497aab] text-white hover:bg-[#5a8bc4] transition-all duration-300 font-medium tracking-wider shadow-lg hover:shadow-xl"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                  Retour au journal
                </Link>
              </div>
            </div>
          </div>
        </article>
      </div>
    </>
  );
};

export default BlogPostPage;

